import { Building, Check, Edit, X } from 'lucide-react'

import {
  type AccountOrganization,
  type Organization,
  type OrganizationDetail,
  Role,
} from '@ankor-io/common/types/account'

import { Badge } from '@/ui-components/Badge'
// import { Avatar } from '@/ui-components/Avatar'
import { Button } from '@/ui-components/Button'
import { DialogBody, DialogTitle } from '@/ui-components/Dialog'

export type OrganizationListModalContentProps = {
  organizations: Record<string, AccountOrganization & Organization>
  activeOrganizationId: string | null
  onClose: () => void
  onSwitchOrganization: (orgId: string) => void
  onEditStart: (org: OrganizationDetail) => void
}

const UNNAMED_ORGANIZATION = 'Unnamed Organization'

const OrganizationListModal = ({
  organizations,
  activeOrganizationId,
  onClose,
  onSwitchOrganization,
  onEditStart,
}: OrganizationListModalContentProps) => {
  const currentOrg = activeOrganizationId ? organizations[activeOrganizationId] : null

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-x-2">
          <Building className="size-5 text-gray-900 dark:text-white" />
          <DialogTitle>Your Organizations</DialogTitle>
        </div>
        <Button
          plain
          onClick={onClose}
          data-testid="close-modal-btn"
        >
          <X data-slot="icon" />
        </Button>
      </div>

      <DialogBody>
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {Object.values(organizations).map((org) => (
            <div
              key={org.id}
              className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg"
            >
              <div className="flex items-center gap-4 flex-1 min-w-0">
                {/* TODO: add logo to organization */}
                {/* <Avatar
                  src={org.logo}
                  initials={org.name.substring(0, 2).toUpperCase()}
                  alt={`${org.name} logo`}
                  className="size-12 shrink-0"
                /> */}

                <div className="flex-1 min-w-0">
                  <div className="flex flex-col mb-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                        {org?.name || UNNAMED_ORGANIZATION}
                      </h3>
                      {org.id === activeOrganizationId && (
                        <Badge color="primary">
                          <Check className="size-3" />
                          Current
                        </Badge>
                      )}
                    </div>
                    {org?.description && <p className="text-sm text-gray-600 dark:text-gray-400">{org.description}</p>}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Role: {org.roles.map((role) => role.charAt(0).toUpperCase() + role.slice(1)).join(', ')}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 ml-4 shrink-0">
                {org.roles.some((role) => [Role.ADMIN, Role.OWNER].includes(role)) && (
                  <Button
                    outline
                    onClick={() => onEditStart(org)}
                    data-testid={`edit-btn-${org.id}`}
                  >
                    <Edit data-slot="icon" />
                  </Button>
                )}

                {org.id !== activeOrganizationId && (
                  <Button
                    outline
                    onClick={() => onSwitchOrganization(org.id)}
                    className="px-4 py-2"
                    data-testid={`switch-btn-${org.id}`}
                  >
                    Switch
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
            You are currently working in {currentOrg?.name || UNNAMED_ORGANIZATION}
          </p>
        </div>
      </DialogBody>
    </>
  )
}

export default OrganizationListModal
